import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Trash2, Mail, MessageCircle, ChevronRight, FileEdit, Info, Clock, AlertTriangle } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";
import { CommsToggleSwitch, DeliverySegmented } from "./shared";
import { PortalCommsSettings } from "./PortalComms";

// Sprint 36.5 — Communications > Automations consolidation.
//
// This page is now purely for enabling automations + choosing their delivery
// channel(s) + template. All timing is owned by Settings > Automation
// Timings (`automation_settings` doc, read by `automation_engine`). Local
// timing pickers have been removed except for appointment reminders where
// multiple simultaneous reminders (48h / 24h / 2h) are legitimately separate
// records.
const PRESETS = [
    // -- Client messaging (portal-owned; live on the top of the page in
    //    `PortalCommsSettings`). Not repeated here.
    // -- Aftercare & follow-up presets --
    {
        trigger: "aftercare_due",
        title: "Aftercare check-in",
        desc: "A day or two after the appointment — how they're healing, what to look out for.",
        wa_automation_id: "aftercare",
        timing_source: "settings",
        timing_summary: (s) => `${describeMinutes(s?.aftercare_delay_minutes)} after checkout`,
    },
    {
        trigger: "review_request_due",
        title: "Ask for a review",
        desc: "A gentle Google / Trustpilot request once they've had time to enjoy the results.",
        wa_automation_id: "review_request",
        timing_source: "settings",
        timing_summary: (s) => reviewSummary(s),
    },
    {
        trigger: "rebooking_reminder_due",
        title: "Rebooking reminder",
        desc: "Encourage clients to book their next appointment before their results wear off.",
        wa_automation_id: "rebook_reminder",
        timing_source: "settings",
        timing_summary: (s) => rebookSummary(s),
    },
    {
        trigger: "deposit_requested",
        title: "Deposit follow-up",
        desc: "Politely remind clients to pay their deposit if they haven't done so at booking.",
        wa_automation_id: "deposit_payment",
        timing_source: "fixed",
        timing_summary: () => "Sent immediately after booking if no deposit",
    },
    {
        trigger: "birthday",
        title: "Birthday message",
        desc: "A warm birthday note — optionally with a small gift or discount.",
        wa_automation_id: "birthday_offer",
        timing_source: "fixed",
        timing_summary: () => "On the customer's birthday",
    },
    {
        trigger: "inactive_customer_winback",
        title: "Win-back for lapsed clients",
        desc: "Reach out to clients you haven't seen in a while with a special invitation.",
        wa_automation_id: "win_back",
        timing_source: "fixed",
        timing_summary: () => "When a client hasn't booked in a while",
    },
];

function describeMinutes(mins) {
    const m = Number(mins || 0);
    if (m === 0) return "Immediately";
    if (m % (60 * 24) === 0) return `${m / (60 * 24)} day${m === 60 * 24 ? "" : "s"}`;
    if (m % 60 === 0) return `${m / 60} hour${m === 60 ? "" : "s"}`;
    return `${m} min`;
}

function reviewSummary(s) {
    const overrides = s?.review_delay_by_category || {};
    const hasCatOverride = Object.keys(overrides).length > 0;
    const defaultTxt = `Default: ${describeMinutes(s?.review_delay_minutes)} after checkout`;
    return hasCatOverride
        ? `Treatment-specific timing · ${defaultTxt}`
        : defaultTxt;
}

function rebookSummary(s) {
    const perCat = s?.rebooking_delay_days_by_category || {};
    const values = Object.values(perCat).filter((v) => Number(v) > 0);
    if (values.length === 0) return "Not configured yet";
    const min = Math.min(...values.map(Number));
    const max = Math.max(...values.map(Number));
    return min === max
        ? `${min} days after checkout`
        : `Treatment-specific timing · ${min}–${max} days after checkout`;
}

export function CommsAutomations() {
    const [rules, setRules] = useState(null);
    const [templates, setTemplates] = useState(null);
    const [automationSettings, setAutomationSettings] = useState(null);
    const [waStatus, setWaStatus] = useState(null);
    const [waAutomationStatuses, setWaAutomationStatuses] = useState({});
    const [error, setError] = useState(null);

    const load = () => {
        setError(null);
        Promise.all([
            staffApi.commsListRules(),
            staffApi.commsListTemplates({ status: "published" }),
            staffApi.getAutomationSettings().catch(() => null),
            staffApi.whatsappStatus().catch(() => null),
            staffApi.whatsappAutomationStatus().catch(() => ({ statuses: {} })),
        ]).then(([r, t, s, wa, waAuto]) => {
            setRules(r.rules || []);
            setTemplates(t.templates || []);
            setAutomationSettings(s);
            setWaStatus(wa);
            setWaAutomationStatuses((waAuto && waAuto.statuses) || {});
        }).catch(setError);
    };
    useEffect(load, []);

    if (error) return <ErrorState error={error} onRetry={load} />;
    if (!rules || !templates) return <Skeleton className="h-64" />;

    const emailAvailable = !!(automationSettings?.channels?.email?.enabled ?? true);
    const waAvailable = !!(automationSettings?.channels?.whatsapp?.enabled) && waStatus?.connection_state === "connected";

    return (
        <div className="space-y-6" data-testid="comms-automations-page">
            {/* Sprint 28 — portal-owned essentials (confirmation / reminders /
                cancellation / reschedule). Timing lives in Settings > Automation
                Timings; here Angela just toggles rules on/off. */}
            <PortalCommsSettings
                rules={rules}
                emailAvailable={emailAvailable}
                waAvailable={waAvailable}
                waAutomationStatuses={waAutomationStatuses}
                onRulesChanged={load}
            />

            <div className="pt-4 border-t hairline">
                <h2 className="font-serif text-2xl text-white">Aftercare & follow-up</h2>
                <p className="text-sm text-white/50 mt-1">
                    Optional client touchpoints beyond the booking journey. Each uses a template you write.
                </p>
            </div>

            {templates.length === 0 && (
                <div className="rounded-2xl border hairline bg-white/[0.02] p-5 flex flex-wrap items-start gap-3" data-testid="automations-no-templates">
                    <Info className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm text-white/85">Some automations need a published template. Rebooking reminders use the standard rebooking message.</p>
                        <p className="text-[12px] text-white/55 mt-1">Templates are the actual words we send. Once you've written one, come back and switch on any of the automations below.</p>
                    </div>
                    <Link to="../templates" className="h-10 px-4 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center gap-2 shrink-0">
                        <FileEdit className="h-4 w-4" /> Write your first template
                    </Link>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="automation-cards">
                {PRESETS.map((preset) => (
                    <AutomationCard
                        key={preset.trigger}
                        preset={preset}
                        rules={rules.filter((r) => r.trigger === preset.trigger)}
                        templates={templates}
                        automationSettings={automationSettings}
                        emailAvailable={emailAvailable}
                        waAvailable={waAvailable}
                        waAutomationState={waAutomationStatuses[preset.wa_automation_id] || { state: "unavailable", label: "Not available" }}
                        onChanged={load}
                    />
                ))}
            </div>
        </div>
    );
}

export function AutomationCard({ preset, rules, templates, automationSettings, emailAvailable, waAvailable, waAutomationState, onChanged }) {
    const [busy, setBusy] = useState(false);
    const activeRule = useMemo(() => rules.find((r) => r.active) || rules[0], [rules]);
    const systemRebooking = preset.trigger === 'rebooking_reminder_due';
    // Legacy checkout reminders default to email when no rule exists.
    const on = systemRebooking && !activeRule ? true : !!(activeRule && activeRule.active);

    // Interpret the persisted channels list. Backwards-compat: an empty or
    // missing list defaults to ["email"].
    const channels = useMemo(() => {
        const raw = (activeRule?.channels || []).map((c) => (c || "").toLowerCase());
        if (raw.length === 0) return ["email"];
        return raw;
    }, [activeRule]);

    const currentDelivery = useMemo(() => {
        const hasEmail = channels.includes("email");
        const hasWa = channels.includes("whatsapp");
        if (hasEmail && hasWa) return "both";
        if (hasWa) return "whatsapp";
        return "email";
    }, [channels]);

    const waReady = waAutomationState.state === "ready";
    const waPreparing = waAutomationState.state === "preparing";
    const waAttention = waAutomationState.state === "needs_attention";

    const setActive = async (nextOn) => {
        if (busy) return;
        setBusy(true);
        try {
            if (systemRebooking) {
                if (rules.length) {
                    for (const rule of rules) await staffApi.commsPatchRule(rule.id, {active:nextOn && rule.id === activeRule.id});
                } else {
                    await staffApi.commsCreateRule({name:preset.title,trigger:preset.trigger,channels:['email'],active:nextOn});
                }
                toast.success(nextOn ? 'Rebooking reminders — ON' : 'Rebooking reminders — OFF');
                onChanged();
                return;
            }
            if (nextOn) {
                if (activeRule) {
                    await staffApi.commsPatchRule(activeRule.id, { active: true });
                } else {
                    // First-time enable: default to Email-only for backwards
                    // compatibility. Angela can promote to WhatsApp / Both once
                    // Meta has approved the corresponding template.
                    const emailTpl = templates.find((t) => t.channel === "email") || templates[0];
                    if (!emailTpl) {
                        toast.error("Please publish a template first.");
                        setBusy(false);
                        return;
                    }
                    await staffApi.commsCreateRule({
                        name: preset.title,
                        trigger: preset.trigger,
                        template_id: emailTpl.id,
                        channels: ["email"],
                        active: true,
                        description: `Auto-generated from '${preset.title}' preset`,
                    });
                }
            } else {
                if (activeRule) await staffApi.commsPatchRule(activeRule.id, { active: false });
            }
            toast.success(nextOn ? `${preset.title} — ON` : `${preset.title} — Paused`);
            onChanged();
        } catch (e) { toast.error(e?.message || "Update failed"); }
        finally { setBusy(false); }
    };

    const setTemplate = async (templateId) => {
        if (!activeRule || busy) return;
        setBusy(true);
        try {
            await staffApi.commsPatchRule(activeRule.id, { template_id: templateId });
            toast.success("Template updated");
            onChanged();
        } catch (e) { toast.error(e?.message || "Update failed"); }
        finally { setBusy(false); }
    };

    const setDelivery = async (choice) => {
        if (!activeRule || busy) return;
        const next = choice === "both" ? ["email", "whatsapp"]
                    : choice === "whatsapp" ? ["whatsapp"]
                    : ["email"];
        setBusy(true);
        try {
            await staffApi.commsPatchRule(activeRule.id, { channels: next });
            toast.success("Delivery updated");
            onChanged();
        } catch (e) { toast.error(e?.message || "Update failed"); }
        finally { setBusy(false); }
    };

    const deleteRule = async () => {
        if (!activeRule) return;
        if (!window.confirm(`Turn off "${preset.title}" and remove its automation?`)) return;
        setBusy(true);
        try {
            await staffApi.commsDeleteRule(activeRule.id);
            toast.success("Removed");
            onChanged();
        } catch (e) { toast.error(e?.message || "Update failed"); }
        finally { setBusy(false); }
    };

    const boundTemplate = activeRule ? templates.find((t) => t.id === activeRule.template_id) : null;
    // Email templates only for the template selector — WhatsApp templates are
    // resolved automatically from the WhatsApp registry.
    const emailTemplates = templates.filter((t) => t.channel === "email");
    const timingText = preset.timing_summary ? preset.timing_summary(automationSettings) : "—";
    const timingShowLink = preset.timing_source === "settings";

    return (
        <div className={`rounded-2xl border p-4 ${on ? "border-gold/40 bg-gold/[0.04]" : "hairline bg-white/[0.02]"}`} data-testid={`automation-${preset.trigger}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <h3 className="font-serif text-lg text-white">{preset.title}</h3>
                    <p className="text-[12px] text-white/60 mt-1 leading-relaxed">{preset.desc}</p>
                </div>
                <CommsToggleSwitch on={on} busy={busy || (!systemRebooking && templates.length === 0)} onToggle={setActive} testId={`toggle-${preset.trigger}`} ariaLabel={preset.title} />
            </div>

            {systemRebooking && <div className="mt-3 space-y-2 text-sm">
                <p className="text-gold">{on ? 'ON' : 'OFF'}{on ? ` · ${currentDelivery === 'both' ? 'Email and WhatsApp' : currentDelivery === 'whatsapp' ? 'WhatsApp' : 'Email'}` : ''}</p>
                {on && !emailAvailable && currentDelivery !== 'whatsapp' && <p>Email delivery is disabled in channel settings.</p>}
                {on && currentDelivery !== 'email' && (!waAvailable || !waReady) && <p>WhatsApp is not ready to send. Check channel setup.</p>}
                <p>{timingText}. Timing sets when to send; this switch controls whether reminders send.</p>
                <Link to="/staff/settings/automations" className="text-gold underline">Edit rebooking timings</Link>
                {!activeRule && <p>Using the existing automatic email reminder. Switch off to pause it.</p>}
            </div>}

            {on && activeRule && (
                <div className="mt-4 space-y-3 pt-3 border-t hairline">
                    {/* Delivery channel selector */}
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1.5">Delivery via</div>
                        <DeliverySegmented
                            value={currentDelivery}
                            onChange={setDelivery}
                            busy={busy}
                            testIdPrefix={`delivery-${preset.trigger}`}
                            emailAvailable={emailAvailable}
                            waAvailable={waAvailable}
                            waReady={waReady}
                            waPreparing={waPreparing}
                            waAttention={waAttention}
                        />
                        {/* Context-aware hint */}
                        <ChannelHint
                            emailAvailable={emailAvailable}
                            waAvailable={waAvailable}
                            waState={waAutomationState.state}
                        />
                    </div>

                    {/* Email template selector (only shown when Email or Both selected) */}
                    {!systemRebooking && (currentDelivery === "email" || currentDelivery === "both") && emailTemplates.length > 0 && (
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">Email template</div>
                            <select value={activeRule.template_id || ""} onChange={(e) => setTemplate(e.target.value)}
                                    data-testid={`template-select-${preset.trigger}`}
                                    className="w-full bg-transparent border hairline rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-gold">
                                {emailTemplates.map((t) => (
                                    <option key={t.id} value={t.id} className="bg-navy">{t.name}</option>
                                ))}
                            </select>
                        </label>
                    )}

                    {/* Timing summary — pulled from Settings > Automation Timings */}
                    <div className="rounded-lg bg-white/[0.03] border hairline p-3">
                        <div className="flex items-start gap-2">
                            <Clock className="h-3.5 w-3.5 text-gold/70 mt-0.5 shrink-0" strokeWidth={1.5} />
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">When to send</div>
                                <div className="text-[12px] text-white/80 mt-0.5">{timingText}</div>
                                {timingShowLink && (
                                    <Link to="/staff/settings/automations" data-testid={`edit-timings-${preset.trigger}`}
                                          className="mt-1 inline-flex items-center gap-1 text-[11px] text-gold hover:underline">
                                        Edit timings <ChevronRight className="h-3 w-3" />
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                        <div className="text-[11px] text-white/50 inline-flex items-center gap-1.5">
                            {systemRebooking ? 'Standard rebooking message' : boundTemplate ? boundTemplate.name : "—"}
                        </div>
                        {!systemRebooking && <button onClick={deleteRule} disabled={busy} data-testid={`delete-${preset.trigger}`}
                                className="text-[11px] text-red-300 hover:text-red-200 inline-flex items-center gap-1">
                            <Trash2 className="h-3 w-3" /> Remove
                        </button>}
                    </div>
                </div>
            )}
        </div>
    );
}


function ChannelHint({ emailAvailable, waAvailable, waState }) {
    // Show a compact, honest hint under the segmented control so Angela
    // understands why an option is disabled. Never exposes Meta terminology.
    if (!waAvailable) {
        return (
            <p className="text-[11px] text-white/45 mt-1.5 inline-flex items-center gap-1">
                <Info className="h-3 w-3" /> Enable WhatsApp in <Link to="/staff/settings/comms-defaults" className="underline hover:text-gold">Communications &amp; Marketing settings</Link> first.
            </p>
        );
    }
    if (waState === "preparing") {
        return (
            <p className="text-[11px] text-white/60 mt-1.5 inline-flex items-center gap-1">
                <Info className="h-3 w-3 text-gold/80" /> WhatsApp is preparing this message. It will be usable shortly.
            </p>
        );
    }
    if (waState === "needs_attention") {
        return (
            <p className="text-[11px] text-amber-200/80 mt-1.5 inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> WhatsApp for this automation needs attention. Email will continue as normal.
            </p>
        );
    }
    if (!emailAvailable) {
        return (
            <p className="text-[11px] text-white/45 mt-1.5 inline-flex items-center gap-1">
                <Info className="h-3 w-3" /> Enable Email in <Link to="/staff/settings/comms-defaults" className="underline hover:text-gold">Communications &amp; Marketing settings</Link> first.
            </p>
        );
    }
    return null;
}
